import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from '@strava/ui/clsx';
import AdyenCheckout from '@adyen/adyen-web';
import I18n from 'utils/I18n';
import '@adyen/adyen-web/dist/adyen.css';

import styles from './AdyenForm.scss';

// AdyenForm supports displaying Adyen dropin
// component. We change prices by updating the amount, sessionId and
// sessionData values. We would do this for going between recurring changes
// on the ui, i.e. monthly to yearly.
const AdyenForm = ({
  sessionData,
  sessionId,
  clientKey,
  environment,
  currency,
  amount,
  promotionCode,
  handleCompleted,
  handleSubmit,
  handleError
}) => {
  const adyenContainer = useRef(null);
  const [checkoutFactory, setCheckoutFactory] = useState(null);
  // Using a reference for the checkout component instead of a state
  // to avoid double rendering of going from monthly to yearly and vice versa
  const checkoutComponentRef = useRef(null);
  // Adyen configuration for component factory.
  // Currently, we are just using the default dropin
  // component. As we iterate, we adjust this configuration
  // to include specific country payment configurations.
  const translations = {};
  const translationKey = promotionCode
    ? 'strava.premium.checkout.payment.submit_cta.start_membership'
    : 'strava.premium.checkout.payment.submit_cta.start_trial';
  translations[I18n.language()] = {
    confirmPreauthorization: I18n.t(translationKey)
  };
  const language = I18n.language();

  const config = {
    environment,
    clientKey,
    analytics: {
      enabled: false
    },
    language,
    translations,
    paymentMethodsConfiguration: {
      paypal: {
        blockPayPalCreditButton: true,
        blockPayPalPayLaterButton: true
      }
    }
  };
  // Creating a reference for the handler to allow
  // to update the handler in the checkout factory
  // which is a singleton.  We pass a handler when we
  // first create the factory so that means the handler
  // passed will have references to data on the initial
  // call to the component.  Using a reference allows
  // to update the handler reference on re-rendering.
  const handleCompletedRef = useRef(handleCompleted);
  handleCompletedRef.current = handleCompleted;
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;
  const isValidSessionId = sessionId !== '' && sessionId !== null;

  // Only set sessionId and sessionData when we
  // have valid data, otherwise factory will fail
  // with error. We can have an empty sessionId
  // when we are minting a new sessionId on the
  // server.
  if (isValidSessionId) {
    config.session = {
      id: sessionId,
      sessionData
    };
    config.amount = {
      value: amount,
      currency
    };
  }

  useEffect(() => {
    const createCheckout = async () => {
      const factory = await AdyenCheckout({
        ...config,
        onPaymentCompleted: (result) => {
          const handler = handleCompletedRef.current;
          handler(result);
        },
        beforeSubmit: (data, component, action) => {
          // this call back will need to be removed once
          // we implement 3ds since that will require
          // using onSubmit which bypasses this handler
          const beforeHandler = handleSubmitRef.current;
          beforeHandler();
          action.resolve(data);
        },
        onError: (error) => {
          const errorHandler = handleErrorRef.current;
          if (error instanceof Error) {
            errorHandler(error);
          } else {
            const errorMessage = JSON.stringify(error);
            const customError = new Error(errorMessage);
            customError.name = 'AdyenCustomError';
            errorHandler(customError);
          }
        }
      });

      return factory;
    };

    createCheckout()
      .then((factory) => {
        setCheckoutFactory(factory);
      })
      .catch((e) => console.log('\terror creating checkout config: %o', e));
  }, []);

  if (checkoutFactory && isValidSessionId) {
    checkoutFactory.update(config);

    if (checkoutComponentRef.current) {
      checkoutFactory.remove(checkoutComponentRef.current);
    }

    checkoutComponentRef.current = checkoutFactory.create('dropin');
    checkoutComponentRef.current.mount(adyenContainer.current);
  }

  return (
    <div className={styles.checkoutForm}>
      <div
        ref={adyenContainer}
        className={clsx('payment', styles.adyenDropIn)}
      />
    </div>
  );
};

AdyenForm.propTypes = {
  sessionData: PropTypes.string.isRequired,
  sessionId: PropTypes.string.isRequired,
  clientKey: PropTypes.string.isRequired,
  environment: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  promotionCode: PropTypes.string,
  handleCompleted: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired
};

AdyenForm.defaultProps = {
  promotionCode: null
};

export default AdyenForm;
